import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  DollarSign,
  Plus,
  Pencil,
  Trash2,
  BookOpen,
  Search,
  CreditCard,
  Receipt,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeesManagementPage() {
  const { user } = useAuth();
  const [classFees, setClassFees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    class_id: "",
    fee_amount: "",
    term: "",
    description: "",
  });

  // Payment state
  const [activeTab, setActiveTab] = useState("fees"); // "fees" | "payments"
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState("");
  const [paymentForm, setPaymentForm] = useState({
    student_id: "",
    amount: "",
    payment_method: "",
    payment_reference: "",
    payment_date: new Date().toISOString().split("T")[0],
    term: "",
    academic_year: "",
  });

  // For filtering students in payment modal
  const [studentSelectSearch, setStudentSelectSearch] = useState("");

  const token = localStorage.getItem("token");

  const fetchClassFees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassFees(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch class fees");
      setClassFees([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch classes");
      setClasses([]);
    }
  }, [token]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setStudents([]);
    }
  }, [token]);

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const res = await axios.get(`${API}/fees/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch payments");
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [token]);

  const fetchActiveTerm = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/active-term`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentForm((prev) => ({
        ...prev,
        term: res.data.active_term || "",
        academic_year: res.data.academic_year || "",
      }));
    } catch {
      // silent
    }
  }, [token]);

  useEffect(() => {
    fetchClassFees();
    fetchClasses();
    fetchStudents();
    fetchPayments();
    fetchActiveTerm();
  }, [fetchClassFees, fetchClasses, fetchStudents, fetchPayments, fetchActiveTerm]);

  const resetForm = () => {
    setFormData({ class_id: "", fee_amount: "", term: "", description: "" });
    setEditingFee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFee) {
        await axios.put(`${API}/fees/${editingFee.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Class fee updated");
      } else {
        await axios.post(`${API}/fees`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Class fee created");
      }
      setIsModalOpen(false);
      resetForm();
      fetchClassFees();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save class fee");
    }
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({
      class_id: fee.class_id,
      fee_amount: fee.fee_amount,
      term: fee.term || "",
      description: fee.description || "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this fee record?")) return;
    try {
      await axios.delete(`${API}/fees/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Fee record deleted");
      fetchClassFees();
    } catch {
      toast.error("Failed to delete fee record");
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/fees/payments`, paymentForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Payment recorded successfully");
      setIsPaymentModalOpen(false);
      setPaymentForm((prev) => ({
        student_id: "",
        amount: "",
        payment_method: "",
        payment_reference: "",
        payment_date: new Date().toISOString().split("T")[0],
        term: prev.term,
        academic_year: prev.academic_year,
      }));
      fetchPayments();
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record payment");
    }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) return;
    try {
      await axios.delete(`${API}/fees/payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Payment deleted");
      fetchPayments();
      fetchStudents();
    } catch {
      toast.error("Failed to delete payment");
    }
  };

  const filteredFees = classFees.filter((f) =>
    (f.class_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (f.term || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (f.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const feesByClass = filteredFees.reduce((acc, fee) => {
    const key = fee.class_name || "Unassigned Class";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(fee);
    return acc;
  }, {});

  const classSectionEntries = Object.entries(feesByClass).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const classNameById = classes.reduce((acc, cls) => {
    acc[cls.id] = cls.display_name || `${cls.grade_level} ${cls.name}`;
    return acc;
  }, {});

  const studentClassById = students.reduce((acc, student) => {
    acc[student.id] =
      student.class_name ||
      classNameById[student.class_id] ||
      "Unassigned Class";
    return acc;
  }, {});

  const filteredPayments = payments.filter((p) => {
    const q = paymentSearch.toLowerCase();
    const className = (studentClassById[p.student_id] || "").toLowerCase();
    return (
      (p.first_name || "").toLowerCase().includes(q) ||
      (p.last_name || "").toLowerCase().includes(q) ||
      (p.student_id_code || "").toLowerCase().includes(q) ||
      (p.payment_reference || "").toLowerCase().includes(q) ||
      (p.payment_method || "").toLowerCase().includes(q) ||
      className.includes(q)
    );
  });

  const paymentsByClassEntries = Object.entries(
    filteredPayments.reduce((acc, payment) => {
      const className = studentClassById[payment.student_id] || "Unassigned Class";
      if (!acc[className]) {
        acc[className] = [];
      }
      acc[className].push(payment);
      return acc;
    }, {})
  ).sort(([a], [b]) => a.localeCompare(b));

  // Group fees by class for summary cards
  const classFeeSummary = classes.map((cls) => {
    const fee = classFees.find((f) => f.class_id === cls.id);
    return { ...cls, fee_amount: fee ? Number(fee.fee_amount) : null };
  });

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const printFeeSections = (sections, selectedClassName = null) => {
    if (!sections.length) {
      toast.error("No fee records available to print");
      return;
    }

    const htmlSections = sections
      .map(([className, records]) => {
        const rows = records
          .map(
            (fee, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${escapeHtml(fee.term || "All terms")}</td>
                <td>$${Number(fee.fee_amount || 0).toFixed(2)}</td>
                <td>${escapeHtml(fee.description || "-")}</td>
              </tr>
            `
          )
          .join("");

        return `
          <section class="class-section">
            <h3>${escapeHtml(className)}</h3>
            <p class="meta">Records: ${records.length}</p>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Term</th>
                  <th>Fee Amount</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </section>
        `;
      })
      .join("");

    const title = selectedClassName
      ? `Fee Records - ${selectedClassName}`
      : "Fee Records by Class";

    const printWindow = window.open("", "_blank", "width=1100,height=800");
    if (!printWindow) {
      toast.error("Unable to open print preview. Please allow popups.");
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(title)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #0f172a; }
            h1 { margin: 0 0 6px; }
            .meta-line { margin: 0 0 20px; color: #475569; font-size: 12px; }
            .class-section { margin-bottom: 28px; break-inside: avoid; }
            .class-section h3 { margin: 0 0 4px; }
            .class-section .meta { margin: 0 0 10px; color: #475569; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; }
            th { background: #f8fafc; }
            @media print {
              body { margin: 12mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .class-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Cresabel School</h1>
          <p class="meta-line">${escapeHtml(title)} | Printed: ${new Date().toLocaleString()}</p>
          ${htmlSections}
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const printPaymentsByClass = () => {
    if (!paymentsByClassEntries.length) {
      toast.error("No payment records available to print");
      return;
    }

    const htmlSections = paymentsByClassEntries
      .map(([className, records]) => {
        const consolidatedRows = Object.values(
          records.reduce((acc, payment) => {
            const studentKey = payment.student_id_code || payment.student_id || payment.id;
            if (!acc[studentKey]) {
              acc[studentKey] = {
                studentKey,
                studentName:
                  `${payment.first_name || ""} ${payment.last_name || ""}`.trim() ||
                  "Unknown Student",
                totalAmount: 0,
                recordCount: 0,
                latestPaymentDate: null,
                methods: new Set(),
                references: [],
              };
            }

            const row = acc[studentKey];
            row.totalAmount += Number(payment.amount || 0);
            row.recordCount += 1;
            if (payment.payment_method) {
              row.methods.add(payment.payment_method);
            }
            if (payment.payment_reference) {
              row.references.push(payment.payment_reference);
            }
            if (payment.payment_date) {
              if (!row.latestPaymentDate || new Date(payment.payment_date) > new Date(row.latestPaymentDate)) {
                row.latestPaymentDate = payment.payment_date;
              }
            }

            return acc;
          }, {})
        ).sort((a, b) => a.studentName.localeCompare(b.studentName));

        const htmlRows = consolidatedRows
          .map(
            (row, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${escapeHtml(row.studentName)}</td>
                <td>${escapeHtml(row.studentKey)}</td>
                <td>$${row.totalAmount.toFixed(2)}</td>
                <td>${row.recordCount}</td>
                <td>${escapeHtml(Array.from(row.methods).join(", ") || "-")}</td>
                <td>${escapeHtml(row.references.join(" ") || "-")}</td>
                <td>${escapeHtml(row.latestPaymentDate ? new Date(row.latestPaymentDate).toLocaleDateString() : "-")}</td>
              </tr>
            `
          )
          .join("");

        return `
          <section class="class-section">
            <h3>${escapeHtml(className)}</h3>
            <p class="meta">Records: ${records.length} | Students: ${consolidatedRows.length}</p>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Student ID</th>
                  <th>Total Paid</th>
                  <th>No. of Records</th>
                  <th>Payment Methods</th>
                  <th>References</th>
                  <th>Latest Payment Date</th>
                </tr>
              </thead>
              <tbody>${htmlRows}</tbody>
            </table>
          </section>
        `;
      })
      .join("");

    const printWindow = window.open("", "_blank", "width=1100,height=800");
    if (!printWindow) {
      toast.error("Unable to open print preview. Please allow popups.");
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Payment Records by Class</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #0f172a; }
            h1 { margin: 0 0 6px; }
            .meta-line { margin: 0 0 20px; color: #475569; font-size: 12px; }
            .class-section { margin-bottom: 28px; break-inside: avoid; }
            .class-section h3 { margin: 0 0 4px; }
            .class-section .meta { margin: 0 0 10px; color: #475569; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; }
            th { background: #f8fafc; }
            @media print {
              body { margin: 12mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .class-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Cresabel School</h1>
          <p class="meta-line">Payment Records by Class (Consolidated by Student ID) | Printed: ${new Date().toLocaleString()}</p>
          ${htmlSections}
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading flex items-center gap-2">
            <DollarSign className="text-pink-500" size={28} />
            Fees Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage class fees and record student payments
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2">
        <Button
          variant={activeTab === "fees" ? "default" : "ghost"}
          className={activeTab === "fees" ? "bg-pink-500 hover:bg-pink-600 text-white" : ""}
          onClick={() => setActiveTab("fees")}
        >
          <BookOpen size={16} className="mr-2" /> Class Fees
        </Button>
        <Button
          variant={activeTab === "payments" ? "default" : "ghost"}
          className={activeTab === "payments" ? "bg-pink-500 hover:bg-pink-600 text-white" : ""}
          onClick={() => setActiveTab("payments")}
        >
          <CreditCard size={16} className="mr-2" /> Record Payments
        </Button>
      </div>

      {activeTab === "fees" ? (
        <>
          {/* Class Fees Tab */}
          <div className="flex justify-end mb-4">
            <Dialog
              open={isModalOpen}
              onOpenChange={(open) => {
                setIsModalOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="bg-pink-500 hover:bg-pink-600 text-white">
                  <Plus size={18} className="mr-2" />
                  Set Class Fee
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[450px]">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingFee ? "Edit Class Fee" : "Set Class Fee"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingFee
                      ? "Update the fee for this class"
                      : "Set the fee amount for a class — it will be applied to all students"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Class</Label>
                    <Select
                      value={formData.class_id}
                      onValueChange={(value) =>
                        setFormData({ ...formData, class_id: value })
                      }
                      required
                      disabled={!!editingFee}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.display_name || `${cls.grade_level} ${cls.name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fee Amount ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.fee_amount}
                      onChange={(e) =>
                        setFormData({ ...formData, fee_amount: e.target.value })
                      }
                      placeholder="500.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Term (optional)</Label>
                    <Select
                      value={formData.term}
                      onValueChange={(value) =>
                        setFormData({ ...formData, term: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Term 1">Term 1</SelectItem>
                        <SelectItem value="Term 2">Term 2</SelectItem>
                        <SelectItem value="Term 3">Term 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optional)</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="e.g. Tuition + Levies"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-pink-500 hover:bg-pink-600"
                    >
                      {editingFee ? "Update" : "Save"} Fee
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

      {/* Class Fee Summary Cards */}
      {classFeeSummary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          {classFeeSummary.map((cls) => (
            <Card key={cls.id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen size={16} className="text-pink-500" />
                  <p className="text-sm font-medium text-slate-700">{cls.display_name || `${cls.grade_level} ${cls.name}`}</p>
                </div>
                {cls.fee_amount != null ? (
                  <p className="text-xl font-bold text-slate-900">
                    ${cls.fee_amount.toFixed(2)}
                  </p>
                ) : (
                  <p className="text-sm text-slate-400 italic">Not set</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by class, term, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => printFeeSections(classSectionEntries)}
          disabled={classSectionEntries.length === 0}
        >
          <Printer size={16} className="mr-2" />
          Print Fees by Class
        </Button>
      </div>

      {/* Fees by Class Sections */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">
            Class Fee Records ({filteredFees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : filteredFees.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
              <p>No fee records found</p>
              <p className="text-sm">Click &quot;Set Class Fee&quot; to get started</p>
            </div>
          ) : (
            <div className="space-y-6">
              {classSectionEntries.map(([className, records]) => (
                <div key={className} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-heading text-base text-slate-900 flex items-center gap-2">
                        <BookOpen size={16} className="text-pink-500" />
                        {className}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">{records.length} fee record(s)</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => printFeeSections([[className, records]], className)}
                    >
                      <Printer size={14} className="mr-2" />
                      Print This Class
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="font-medium text-slate-500">Fee Amount</TableHead>
                          <TableHead className="font-medium text-slate-500">Term</TableHead>
                          <TableHead className="font-medium text-slate-500">Description</TableHead>
                          <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {records.map((fee) => (
                          <TableRow key={fee.id} className="table-row-hover">
                            <TableCell className="font-mono font-bold text-slate-900">
                              ${Number(fee.fee_amount).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {fee.term ? (
                                <Badge variant="secondary" className="bg-slate-100 text-xs">
                                  {fee.term}
                                </Badge>
                              ) : (
                                <span className="text-slate-400 text-sm">All terms</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {fee.description || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(fee)}
                                  className="text-slate-500 hover:text-pink-500"
                                >
                                  <Pencil size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(fee.id)}
                                  className="text-slate-500 hover:text-red-500"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </>
      ) : (
        <>
          {/* Payments Tab */}
          <div className="flex justify-between items-center mb-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search by student name, ID, reference..."
                value={paymentSearch}
                onChange={(e) => setPaymentSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-pink-500 hover:bg-pink-600 text-white ml-4">
                  <Plus size={18} className="mr-2" />
                  Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="font-heading">Record Student Payment</DialogTitle>
                  <DialogDescription>
                    Record a fee payment for a student
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePaymentSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Student</Label>
                    <Input
                      type="text"
                      placeholder="Type to filter by name or ID..."
                      value={studentSelectSearch}
                      onChange={e => setStudentSelectSearch(e.target.value)}
                      className="mb-2"
                    />
                    <Select
                      value={paymentForm.student_id}
                      onValueChange={(value) =>
                        setPaymentForm({ ...paymentForm, student_id: value })
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a student" />
                      </SelectTrigger>
                      <SelectContent>
                        {students
                          .filter(s => {
                            const q = studentSelectSearch.trim().toLowerCase();
                            return (
                              !q ||
                              s.first_name?.toLowerCase().includes(q) ||
                              s.last_name?.toLowerCase().includes(q) ||
                              s.student_id_code?.toLowerCase().includes(q)
                            );
                          })
                          .map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.first_name} {s.last_name} ({s.student_id_code})
                              {s.total_fee ? ` — Bal: $${(Number(s.total_fee) - Number(s.amount_paid || 0)).toFixed(2)}` : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={paymentForm.amount}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, amount: e.target.value })
                        }
                        placeholder="100.00"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Date</Label>
                      <Input
                        type="date"
                        value={paymentForm.payment_date}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, payment_date: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select
                        value={paymentForm.payment_method}
                        onValueChange={(value) =>
                          setPaymentForm({ ...paymentForm, payment_method: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                          <SelectItem value="EcoCash">EcoCash</SelectItem>
                          <SelectItem value="OneMoney">OneMoney</SelectItem>
                          <SelectItem value="InnBucks">InnBucks</SelectItem>
                          <SelectItem value="Cheque">Cheque</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Reference</Label>
                      <Input
                        value={paymentForm.payment_reference}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, payment_reference: e.target.value })
                        }
                        placeholder="e.g. TXN-12345"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Term</Label>
                      <Select
                        value={paymentForm.term}
                        onValueChange={(value) =>
                          setPaymentForm({ ...paymentForm, term: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select term" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Term 1">Term 1</SelectItem>
                          <SelectItem value="Term 2">Term 2</SelectItem>
                          <SelectItem value="Term 3">Term 3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Academic Year</Label>
                      <Input
                        value={paymentForm.academic_year}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, academic_year: e.target.value })
                        }
                        placeholder="2026"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsPaymentModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-pink-500 hover:bg-pink-600">
                      Record Payment
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Payments Table */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Receipt size={20} className="text-pink-500" />
                  Payment Records by Class ({filteredPayments.length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={printPaymentsByClass}
                  disabled={paymentsByClassEntries.length === 0}
                >
                  <Printer size={14} className="mr-2" />
                  Print by Class
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {paymentsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                </div>
              ) : filteredPayments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No payments recorded yet</p>
                  <p className="text-sm">Click &quot;Record Payment&quot; to get started</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {paymentsByClassEntries.map(([className, records]) => (
                    <div key={className} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-heading text-base text-slate-900 flex items-center gap-2">
                          <BookOpen size={16} className="text-pink-500" />
                          {className}
                        </h3>
                        <span className="text-xs text-slate-500">{records.length} record(s)</span>
                      </div>

                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                              <TableHead className="font-medium text-slate-500">Student</TableHead>
                              <TableHead className="font-medium text-slate-500">Amount</TableHead>
                              <TableHead className="font-medium text-slate-500">Method</TableHead>
                              <TableHead className="font-medium text-slate-500">Reference</TableHead>
                              <TableHead className="font-medium text-slate-500">Receipt #</TableHead>
                              <TableHead className="font-medium text-slate-500">Date</TableHead>
                              <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {records.map((p) => (
                              <TableRow key={p.id} className="table-row-hover">
                                <TableCell className="font-medium text-slate-900">
                                  {p.first_name} {p.last_name}
                                  <span className="block text-xs text-slate-400">{p.student_id_code}</span>
                                </TableCell>
                                <TableCell className="font-mono font-bold text-emerald-600">
                                  ${Number(p.amount).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {p.payment_method ? (
                                    <Badge variant="secondary" className="bg-slate-100 text-xs">
                                      {p.payment_method}
                                    </Badge>
                                  ) : "—"}
                                </TableCell>
                                <TableCell className="text-sm text-slate-600">
                                  {p.payment_reference || "—"}
                                </TableCell>
                                <TableCell className="text-sm text-slate-600 font-mono">
                                  {p.receipt_number || "—"}
                                </TableCell>
                                <TableCell className="text-sm text-slate-600">
                                  {new Date(p.payment_date).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeletePayment(p.id)}
                                    className="text-slate-500 hover:text-red-500"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
